import * as THREE from 'three';
import { Logger } from "../core/Logger.js";

export class TouchInput {

    constructor(renderer) {
        this._touches = new Map();
        this._touchPosition = new THREE.Vector2();
        this._touchPosition3D = new THREE.Vector3();
        this._raycaster = new THREE.Raycaster();
        this._plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), -0.5);
        this._renderer = renderer;
        this._frameSnapshot = null;
    }

    _updateTouches(event, phase) {
        const touches = Array.from(event.changedTouches);

        for (const touch of touches) {
            const touchData = {
                id: touch.identifier,
                x: touch.clientX,
                y: touch.clientY,
                target: touch.target,
                phase: phase,
                original: touch
            };

            if (phase === 'end' || phase === 'cancel') {
                setTimeout(() => this._touches.delete(touch.identifier), 100);
            }

            this._touches.set(touch.identifier, touchData);
        }
    }

    _onTouchStart(event) {
        this._updateTouches(event, 'start');
    }

    _onTouchMove(event) {
        this._updateTouches(event, 'move');
    }

    _onTouchEnd(event) {
        this._updateTouches(event, 'end');
    }

    _onTouchCancel(event) {
        this._updateTouches(event, 'cancel');
    }

    /**
     * Starts capturing touch events.
     */
    start() {
        if (typeof window === 'undefined') {
            Logger.warn('Window is not defined. Event listeners cannot be registered.');
            return;
        }

        this._onTouchStart = this._onTouchStart.bind(this);
        this._onTouchMove = this._onTouchMove.bind(this);
        this._onTouchEnd = this._onTouchEnd.bind(this);
        this._onTouchCancel = this._onTouchCancel.bind(this);

        window.addEventListener('touchstart', this._onTouchStart, false);
        window.addEventListener('touchmove', this._onTouchMove, false);
        window.addEventListener('touchend', this._onTouchEnd, false);
        window.addEventListener('touchcancel', this._onTouchCancel, false);
    }

    /**
    * Stop capturing touch events.
    */
    stop() {
        this._touches.clear();

        window.removeEventListener('touchstart', this._onTouchStart, false);
        window.removeEventListener('touchmove', this._onTouchMove, false);
        window.removeEventListener('touchend', this._onTouchEnd, false);
        window.removeEventListener('touchcancel', this._onTouchCancel, false);
    }

    /**
     * Ensures that the snapshot is created once per frame.
     */
    _ensureSnapshot() {
        if (this._frameSnapshot) return;

        this._frameSnapshot = {
            touches: Array.from(this._touches.values()).map(t => ({ ...t }))
        };
    }

    /**
     *  Returns the number of active touch points currently being tracked.
     */
    count() {
        this._ensureSnapshot();
        return this._frameSnapshot.touches.length;
    }

    /**
     * Returns either all active touch points or a specific one by index.
     * @param {Number} touchIndex If provided and valid, returns the touch at that index.
     */
    touches(touchIndex = undefined) {
        this._ensureSnapshot();
        const touches = this._frameSnapshot.touches;
        if (touchIndex !== undefined && touches[touchIndex]) {
            return touches[touchIndex];
        } else {
            return touches;
        }
    }

    /**
     * Projects a touch point into 3D space by converting screen coordinates into a ray using the camera and renderer
     * @param {Number} touchIndex Index of the touch point to raycast from.
     * @param {*} camera  The camera to use for raycasting.
     */
    raycast(camera, touchIndex = 0) {
        this._ensureSnapshot();
        const touches = this._frameSnapshot.touches;
        const currentTouch = touches[touchIndex];
        if (!currentTouch) return null;

        const rect = this._renderer.domElement.getBoundingClientRect();
        this._touchPosition.x = ((currentTouch.x - rect.left) / rect.width) * 2 - 1;
        this._touchPosition.y = -((currentTouch.y - rect.top) / rect.height) * 2 + 1;
        this._raycaster.setFromCamera(this._touchPosition, camera);
        this._raycaster.ray.intersectPlane(this._plane, this._touchPosition3D);

        return {
            position: this._touchPosition3D.clone(),
            raycaster: this._raycaster
        };
    }

    isSupported() {
        if (typeof window !== 'undefined') {
            return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        }
        return false;
    }

    /**
     * Call this once per frame to reset the snapshot.
     */
    eventFrameEnd() {
        this._frameSnapshot = null;
    }
}
